// Budgets tab: types, API calls and pure helpers. budgets.test.ts covers the helpers;
// the components only render what these return.
import config from "./config.json";

export type BudgetStatus = "green" | "amber" | "red" | "none";

export interface ScopeLine {
  organization: string;
  customer: string;
  categories: string[];
  services: string[];
}

export interface AlertSent { threshold: number; sent_at: string; }

export interface BudgetSummary {
  id: number;
  name: string;
  note: string;
  amount_usd: number;
  budget_usd: number | null;
  spend_usd: number;
  pct: number | null;
  status: BudgetStatus;
  data_through: string | null;
  start_date: string;
  thresholds: number[];
  alerts_this_month: AlertSent[];
  followers: number;
  following: boolean;
  forecast_usd: number | null;      // month-end forecast; null before the 8th, without data or a budget
  forecast_pct: number | null;
  pace_usd: number | null;          // average per day over the last 7 data days
  forecast_alert_sent_at: string | null;
}

export interface BudgetListResponse { month: string; budgets: BudgetSummary[]; }

export interface BudgetDetail extends BudgetSummary {
  scope: ScopeLine[];
  created_by: string;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
  follower_emails: string[];
  daily: { date: string; cost: number; cumulative: number }[];
  breakdown: { organization: string; customer: string; category: string; cost: number }[];
  months: { month: string; spend_usd: number; budget_usd: number | null }[];
}

export interface BudgetDraft {
  name: string;
  note: string;
  amount: string;
  start_date: string;
  thresholds: number[];
  scope: ScopeLine[];
  follow: boolean;
}

export interface OptionsTree {
  organizations: { name: string; customers: { name: string; categories: { name: string; services: string[] }[] }[] }[];
}

export interface PreviewMonth { month: string; spend_usd: number; pct: number | null; }
export interface PreviewResponse { months: PreviewMonth[]; data_through: string | null; }

export const DEFAULT_THRESHOLDS = [80, 100];
export const STATUS_COLOR: Record<BudgetStatus, string> = { green: "#2e7d32", amber: "#ed6c02", red: "#d32f2f", none: "#9e9e9e" };
export const STATUS_BG: Record<BudgetStatus, string> = { green: "#e8f5e9", amber: "#fff3e0", red: "#ffebee", none: "#f1f5f9" };
export const STATUS_TEXT: Record<BudgetStatus, string> = { green: "#1b5e20", amber: "#b45309", red: "#b71c1c", none: "#475569" };
export const FORECAST_FROM_DAY = 8;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September",
  "October", "November", "December"];

const parseIso = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return { y, m, d };
};
const pad = (n: number) => String(n).padStart(2, "0");
const ordinal = (n: number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

export const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
export const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

export const fmtUsd = (n: number): string => {
  const value = Number.isFinite(n) ? n : 0;
  const cents = Math.round(Math.abs(value) * 100);
  const whole = Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${value < 0 ? "-" : ""}$${whole}.${pad(cents % 100)}`;
};
export const fmtBudget = (n: number): string => { const t = fmtUsd(n); return t.endsWith(".00") ? t.slice(0, -3) : t; };
export const monthName = (iso: string) => MONTHS[parseIso(iso).m - 1];
export const dayLabel = (iso: string | null) => (iso ? `${parseIso(iso).d} ${monthName(iso).slice(0, 3)}` : "no data yet");

export const firstMonthBudget = (amount: number, startIso: string): number => {
  const { y, m, d } = parseIso(startIso);
  const days = daysInMonth(y, m);
  return Math.round(((amount * (days - d + 1)) / days) * 100) / 100;
};

export const prorationHint = (amount: number, startIso: string): string | null => {
  if (!startIso || !(amount > 0)) return null;
  const { y, m, d } = parseIso(startIso);
  if (d === 1) return null;
  const days = daysInMonth(y, m);
  return `${MONTHS[m - 1]} counts from the ${ordinal(d)}: ${fmtUsd(firstMonthBudget(amount, startIso))} `
    + `(${days - d + 1} of ${days} days). Full ${fmtBudget(amount)} from ${MONTHS[m % 12]}.`;
};

export const statusChipLabel = (b: BudgetSummary): string => {
  if (b.pct === null) return `starts ${dayLabel(b.start_date)}`;
  const pct = `${Math.round(b.pct)}%`;
  if (b.pct >= 100) return `${pct} · over budget`;
  const sent = b.alerts_this_month.map((a) => a.threshold).filter((t) => t < 100);
  return sent.length ? `${pct} · ${Math.max(...sent)}% alert sent` : pct;
};

const STATUS_ORDER: Record<BudgetStatus, number> = { red: 0, amber: 1, green: 2, none: 3 };
export const sortBudgets = (list: BudgetSummary[]) =>
  [...list].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name));

export const followersTooltip = (emails: string[]) =>
  emails.length ? `Followed by ${emails.join(", ")}` : "Nobody follows this budget yet";

export const statusOf = (pct: number | null, thresholds: number[]): BudgetStatus =>
  pct === null ? "none" : pct >= 100 ? "red" : thresholds.length && pct >= Math.min(...thresholds) ? "amber" : "green";

export const daysToGoLabel = (dataThroughIso: string): string => {
  const { y, m, d } = parseIso(dataThroughIso);
  const left = daysInMonth(y, m) - d;
  return left <= 0 ? "month complete" : left === 1 ? "1 day to go" : `${left} days to go`;
};

// The caption under the summary bar for the month on screen; parts without data are
// left out. Early in a month the data can still end in the month before: then the
// whole month is still to go.
export const summaryParts = (b: BudgetSummary, monthIso: string): string[] => {
  const parts: string[] = [];
  const { y, m } = parseIso(monthIso);
  const inMonth = !!b.data_through && b.data_through.slice(0, 7) === monthIso.slice(0, 7);
  if (b.budget_usd != null && b.pct != null) {
    const left = b.budget_usd - b.spend_usd;
    parts.push(left < 0 ? `${fmtUsd(-left)} over` : `${fmtUsd(left)} left`);
  }
  if (b.data_through) parts.push(inMonth ? daysToGoLabel(b.data_through) : `${daysInMonth(y, m)} days to go`);
  if (b.forecast_usd != null && b.forecast_pct != null) {
    if (inMonth && daysToGoLabel(b.data_through as string) !== "month complete")
      parts.push(`forecast ${fmtUsd(b.forecast_usd)} (${Math.round(b.forecast_pct)}%)`);
  } else if (b.pct != null && b.data_through && (!inMonth || parseIso(b.data_through).d < FORECAST_FROM_DAY)) {
    parts.push(`forecast from ${FORECAST_FROM_DAY} ${MONTHS[m - 1].slice(0, 3)}`);
  }
  for (const a of b.alerts_this_month) parts.push(`${a.threshold}% alert ${dayLabel(a.sent_at)}`);
  if (b.forecast_alert_sent_at) parts.push(`forecast alert ${dayLabel(b.forecast_alert_sent_at)}`);
  parts.push(`data through ${dayLabel(b.data_through)}`);
  return parts;
};

// Summary bar geometry in % of its width: the scale grows past 100 % with the
// forecast (at most 200 %), so the forecast dot always fits.
export const summaryBar = (pct: number | null, forecastPct: number | null, thresholds: number[]) => {
  const scale = Math.min(200, Math.max(100, forecastPct ?? 0, pct ?? 0));
  const at = (v: number) => Math.round((Math.max(0, Math.min(v, scale)) / scale) * 1000) / 10;
  return {
    fill: at(pct ?? 0),
    forecast: forecastPct === null ? null : at(forecastPct),
    ticks: thresholds.filter((t) => t <= scale).map(at),
  };
};

export const parseThreshold = (text: string): number | null => {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= 500 ? n : null;
};

export const scopeIsComplete = (scope: ScopeLine[]) => scope.length > 0 && scope.every((l) => l.organization && l.customer);

export const validateDraft = (d: BudgetDraft): string | null => {
  if (!d.name.trim()) return "Give the budget a name.";
  if (d.name.trim().length > 80) return "The name can have at most 80 characters.";
  if (d.note.trim().length > 200) return "The note can have at most 200 characters.";
  const amount = Number(d.amount);
  if (!d.amount.trim() || !Number.isFinite(amount) || amount <= 0 || amount > 1_000_000)
    return "The amount must be more than 0 and at most 1,000,000.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.start_date)) return "Pick a start date.";
  if (d.scope.length === 0) return "Add at least one customer under What counts.";
  if (!scopeIsComplete(d.scope)) return "Every What counts line needs an organization and a customer.";
  if (d.thresholds.length === 0 || d.thresholds.length > 6) return "Keep 1 to 6 alert thresholds.";
  return null;
};

export const emptyDraft = (today: string): BudgetDraft => ({
  name: "", note: "", amount: "", start_date: today, thresholds: [...DEFAULT_THRESHOLDS], follow: true,
  scope: [{ organization: "", customer: "", categories: [], services: [] }],
});

export const draftFromDetail = (d: BudgetDetail): BudgetDraft => ({
  name: d.name, note: d.note, amount: String(d.amount_usd), start_date: d.start_date,
  thresholds: [...d.thresholds], follow: d.following,
  scope: d.scope.map((l) => ({ ...l, categories: [...l.categories], services: [...l.services] })),
});

export const draftToBody = (d: BudgetDraft) => ({
  name: d.name.trim(), note: d.note.trim(), amount_usd: Number(d.amount), start_date: d.start_date,
  thresholds: d.thresholds, scope: d.scope, follow: d.follow,
});

export const previewHint = (p: PreviewResponse, thresholds: number[]): string | null => {
  const past = p.months.slice(0, -1).filter((m) => m.pct !== null);
  if (past.length === 0 || thresholds.length === 0) return null;
  const first = Math.min(...thresholds);
  const hits = past.filter((m) => (m.pct as number) >= first).length;
  const span = past.length === 1 ? "last month" : `the last ${past.length === 2 ? "two" : past.length} months`;
  if (hits === 0) return `At this level, the ${first}% alert would not have gone out in ${span}.`;
  if (hits === past.length) {
    return past.length === 1
      ? `At this level, the ${first}% alert would have gone out last month.`
      : `At this level, the ${first}% alert would have gone out in each of ${span}.`;
  }
  return `At this level, the ${first}% alert would have gone out in ${hits} of ${span}.`;
};

const withCurrent = (names: string[], current: string | string[]): string[] => {
  const extra = (Array.isArray(current) ? current : [current]).filter((c) => c && !names.includes(c));
  return Array.from(new Set([...names, ...extra])).sort((a, b) => a.localeCompare(b));
};
const orgNode = (t: OptionsTree | null, org: string) => t?.organizations.find((o) => o.name === org);
const customerNode = (t: OptionsTree | null, org: string, customer: string) =>
  orgNode(t, org)?.customers.find((c) => c.name === customer);

export const organizationsOf = (t: OptionsTree | null, current: string) =>
  withCurrent((t?.organizations ?? []).map((o) => o.name), current);
export const customersOf = (t: OptionsTree | null, org: string, current: string) =>
  withCurrent((orgNode(t, org)?.customers ?? []).map((c) => c.name), current);
export const categoriesOf = (t: OptionsTree | null, org: string, customer: string, current: string[]) =>
  withCurrent((customerNode(t, org, customer)?.categories ?? []).map((c) => c.name), current);
export const servicesOf = (t: OptionsTree | null, org: string, customer: string, categories: string[], current: string[]) =>
  withCurrent((customerNode(t, org, customer)?.categories ?? [])
    .filter((c) => categories.includes(c.name)).flatMap((c) => c.services), current);

export const budgetChartOption = (
  d: { daily: BudgetDetail["daily"]; budget_usd: number | null; thresholds: number[]; forecast_usd?: number | null },
  monthIso: string,
) => {
  const { y, m } = parseIso(monthIso);
  const short = MONTHS[m - 1].slice(0, 3);
  const labels = Array.from({ length: daysInMonth(y, m) }, (_, i) => `${i + 1} ${short}`);
  const byDay = new Map(d.daily.map((p) => [parseIso(p.date).d, p] as const));
  const bars = labels.map((_, i) => byDay.get(i + 1)?.cost ?? null);
  const totals = labels.map((_, i) => byDay.get(i + 1)?.cumulative ?? null);
  // Dashed forecast from the last data day to the month end, straight between spend and forecast.
  const lastDay = d.daily.length ? parseIso(d.daily[d.daily.length - 1].date).d : 0;
  const spent = d.daily.length ? d.daily[d.daily.length - 1].cumulative : 0;
  const ahead = d.forecast_usd != null && lastDay > 0 && lastDay < labels.length ? d.forecast_usd : null;
  const forecastLine = labels.map((_, i) => (ahead === null || i + 1 < lastDay ? null
    : Math.round((spent + ((ahead - spent) * (i + 1 - lastDay)) / (labels.length - lastDay)) * 100) / 100));
  const budget = d.budget_usd;
  const limits = budget === null ? [] : d.thresholds.map((t) => ({ t, value: Math.round(budget * t) / 100 }));
  const colorOf = (t: number) => (t >= 100 ? STATUS_COLOR.red : STATUS_COLOR.amber);
  const markLines = limits.map(({ t, value }) => ({
    yAxis: value,
    lineStyle: { type: "dashed", color: colorOf(t) },
    label: { formatter: `${t}% · ${fmtBudget(value)}`, position: "insideEndTop", color: colorOf(t) },
  }));
  const markPoints = limits.flatMap(({ t, value }) => {
    const hit = d.daily.find((p) => p.cumulative >= value);
    return hit
      ? [{ name: `${t}%`, coord: [labels[parseIso(hit.date).d - 1], hit.cumulative], itemStyle: { borderColor: colorOf(t) } }]
      : [];
  });
  const top = Math.max(budget ?? 0, ahead ?? 0, ...d.daily.map((p) => p.cumulative));
  return {
    grid: { left: 64, right: 24, top: 28, bottom: 36 },
    legend: { show: false },
    tooltip: {
      trigger: "axis",
      formatter: (params: any) => {
        const i = (Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0;
        if (bars[i] === null) {
          const f = forecastLine[i];
          return f === null ? `${labels[i]}<br/>no data yet` : `${labels[i]}<br/>forecast ${fmtUsd(f)}`;
        }
        return `${labels[i]}<br/>day ${fmtUsd(bars[i] ?? 0)} · total ${fmtUsd(totals[i] ?? 0)}`;
      },
    },
    xAxis: { type: "category", data: labels, axisLabel: { fontSize: 11, hideOverlap: true } },
    yAxis: { type: "value", min: 0, max: Math.ceil(top * 1.1) || 1, axisLabel: { fontSize: 11, formatter: (v: number) => fmtBudget(v) } },
    series: [
      { name: "cost per day", type: "bar", data: bars, barMaxWidth: 14, itemStyle: { color: "#90caf9" } },
      {
        name: "running total", type: "line", data: totals, showSymbol: false,
        lineStyle: { width: 2.5, color: "#1565c0" }, itemStyle: { color: "#1565c0" },
        markLine: { symbol: "none", silent: true, data: markLines },
        markPoint: { symbol: "circle", symbolSize: 12, label: { show: false },
          itemStyle: { color: "#ffffff", borderWidth: 2.5 }, data: markPoints },
      },
      { name: "forecast", type: "line", data: forecastLine, showSymbol: false, silent: true,
        lineStyle: { width: 2, type: "dashed", color: "#1565c0" }, itemStyle: { color: "#1565c0" } },
    ],
  };
};

// Last 6 months: spend per month coloured by its status, a dash at the month's budget.
export const monthsChartOption = (months: BudgetDetail["months"], thresholds: number[]) => {
  const labels = months.map((m) => MONTHS[parseIso(m.month).m - 1].slice(0, 3));
  const pcts = months.map((m) => (m.budget_usd ? (m.spend_usd / m.budget_usd) * 100 : null));
  const top = Math.max(1, ...months.map((m) => Math.max(m.spend_usd, m.budget_usd ?? 0)));
  return {
    grid: { left: 56, right: 12, top: 16, bottom: 28 },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params: any) => {
        const i = (Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0;
        const m = months[i];
        return m.budget_usd === null
          ? `${labels[i]} · ${fmtUsd(m.spend_usd)} · before start`
          : `${labels[i]} · ${fmtUsd(m.spend_usd)} of ${fmtBudget(m.budget_usd)} · ${Math.round(pcts[i] ?? 0)}%`;
      },
    },
    xAxis: { type: "category", data: labels, axisLabel: { fontSize: 11 } },
    yAxis: { type: "value", min: 0, max: Math.ceil(top * 1.1),
      axisLabel: { fontSize: 11, formatter: (v: number) => fmtBudget(v) } },
    series: [
      { name: "spent", type: "bar", barMaxWidth: 28,
        data: months.map((m, i) => ({ value: m.spend_usd,
          itemStyle: { color: m.budget_usd === null ? "#cfd8dc" : STATUS_COLOR[statusOf(pcts[i], thresholds)] } })) },
      { name: "budget", type: "scatter", symbol: "rect", symbolSize: [26, 2], z: 3,
        itemStyle: { color: "#37474f" }, data: months.map((m) => m.budget_usd) },
    ],
  };
};

export interface BudgetApi {
  list(): Promise<BudgetListResponse>;
  get(id: number): Promise<BudgetDetail>;
  create(d: BudgetDraft): Promise<BudgetDetail>;
  update(id: number, d: BudgetDraft): Promise<BudgetDetail>;
  remove(id: number): Promise<void>;
  follow(id: number, on: boolean): Promise<boolean>;
  options(): Promise<OptionsTree>;
  preview(scope: ScopeLine[], amount: number, startDate: string): Promise<PreviewResponse>;
}

export const budgetApi = (apiKey: string, idToken: string, baseUrl: string = (config as any).api.chatbot.url): BudgetApi => {
  const call = async <T,>(path: string, method = "GET", body?: unknown): Promise<T> => {
    const res = await fetch(`${baseUrl}/admin/budgets${path}`, {
      method,
      headers: { "Content-Type": "application/json", "X-Api-Key": apiKey, Authorization: idToken },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any).message || `Request failed (${res.status})`);
    return data as T;
  };
  return {
    list: () => call<BudgetListResponse>(""),
    get: (id) => call<BudgetDetail>(`/${id}`),
    create: (d) => call<BudgetDetail>("", "POST", draftToBody(d)),
    update: (id, d) => call<BudgetDetail>(`/${id}`, "PUT", draftToBody(d)),
    remove: async (id) => { await call(`/${id}`, "DELETE"); },
    follow: async (id, on) => (await call<{ following: boolean }>(`/${id}/follow`, on ? "PUT" : "DELETE")).following,
    options: () => call<OptionsTree>("/options"),
    preview: (scope, amount, startDate) =>
      call<PreviewResponse>("/preview", "POST", { scope, amount_usd: amount, start_date: startDate }),
  };
};
